import React, { useEffect, useMemo, useState } from 'react';
import { billingAPI } from '../../services/api';
import StripeCardSetup from '../../components/StripeCardSetup';
import StripePaymentButton from '../../components/StripePaymentButton';
import './StudentPages.css';

const formatPhp = (amount) => new Intl.NumberFormat('en-PH', {
  style: 'currency',
  currency: 'PHP',
  maximumFractionDigits: 0
}).format(Number(amount || 0));

const BillingSetup = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [publishableKey, setPublishableKey] = useState('');

  const currentMonth = useMemo(() => {
    const now = new Date();
    return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
  }, []);

  const loadBillingData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [statusRes, summaryRes, historyRes, keyRes] = await Promise.all([
        billingAPI.getStatus(),
        billingAPI.getMonthlySummary({ month: currentMonth }),
        billingAPI.getHistory(),
        billingAPI.getPublishableKey()
      ]);

      setStatus(statusRes.data);
      setSummary(summaryRes.data);
      setHistory(historyRes.data.invoices || []);
      setPublishableKey(keyRes.data.publishableKey || '');
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Billing data could not be loaded');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBillingData();
  }, [currentMonth]);

  if (loading) {
    return (
      <div className="student-container">
        <div className="flex-center" style={{ minHeight: '40vh' }}>
          <div className="loading-spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="student-container">
      <div className="card-setup-container" style={{ maxWidth: 960, margin: '2rem auto', padding: '2rem', background: '#fff', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <h1>Billing Center</h1>
        <p style={{ fontSize: '1.1rem', lineHeight: '1.6', color: '#555' }}>
          Save your card securely with <strong>Stripe</strong> and pay your lesson balance in Philippine pesos.
        </p>
        <p style={{ color: '#666', marginBottom: '1.5rem' }}>
          Charges are added after each completed lesson. A saved card is also used for automatic collections when enabled.
        </p>

        {error && (
          <div style={{ color: '#dc2626', marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        {!status?.stripeConfigured ? (
          <div style={{ border: '1px solid #f5c36b', background: '#fff8e7', padding: '1rem', borderRadius: 8, marginBottom: '1.5rem', color: '#8a5a00' }}>
            Stripe is not configured yet. Please contact support.
          </div>
        ) : !status?.hasPaymentMethod ? (
          <div style={{ border: '1px solid #d8e2ff', background: '#f7f9ff', padding: '1rem', borderRadius: 8, marginBottom: '1.5rem' }}>
            <h3 style={{ marginTop: 0 }}>Save your payment method</h3>
            <p style={{ color: '#555' }}>Add a card to enable Stripe payments and monthly auto-collection.</p>
            <StripeCardSetup
              publishableKey={publishableKey}
              onSuccess={loadBillingData}
              buttonLabel="Save card with Stripe"
            />
          </div>
        ) : null}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          <div style={{ border: '1px solid #eee', borderRadius: 8, padding: '1rem' }}>
            <div style={{ fontSize: 13, color: '#666' }}>Current balance</div>
            <div style={{ fontSize: 32, fontWeight: 700, margin: '0.5rem 0' }}>{formatPhp(summary?.totalAmount || 0)}</div>
            <div style={{ color: '#666' }}>{summary?.month || currentMonth}</div>
          </div>
          <div style={{ border: '1px solid #eee', borderRadius: 8, padding: '1rem' }}>
            <div style={{ fontSize: 13, color: '#666' }}>Saved card</div>
            <div style={{ fontSize: 24, fontWeight: 700, margin: '0.5rem 0' }}>
              {status?.hasPaymentMethod ? 'Active' : 'Not set'}
            </div>
            <div style={{ color: '#666' }}>
              {status?.paymentMethodSummary
                ? `${String(status.paymentMethodSummary.brand || '').toUpperCase()} •••• ${status.paymentMethodSummary.last4}`
                : 'Add a card to start payments'}
            </div>
          </div>
        </div>

        <div style={{ border: '1px solid #eee', borderRadius: 8, padding: '1rem', marginBottom: '1.5rem' }}>
          <h2 style={{ marginTop: 0 }}>Stripe Payment</h2>
          {!status?.hasPaymentMethod ? (
            <p style={{ color: '#666' }}>Available after you save your card.</p>
          ) : summary?.totalAmount > 0 ? (
            <StripePaymentButton
              publishableKey={publishableKey}
              month={summary.month}
              totalAmount={summary.totalAmount}
              onSuccess={loadBillingData}
            />
          ) : (
            <p style={{ color: '#2d7a46', marginBottom: 0 }}>You have no outstanding balance.</p>
          )}
        </div>

        {status?.hasPaymentMethod && status?.stripeConfigured && (
          <div style={{ border: '1px solid #eee', borderRadius: 8, padding: '1rem', marginBottom: '1.5rem' }}>
            <h2 style={{ marginTop: 0 }}>Update saved card</h2>
            <p style={{ color: '#666' }}>Replace the card used for future Stripe payments and auto-collection.</p>
            <StripeCardSetup
              publishableKey={publishableKey}
              onSuccess={loadBillingData}
              buttonLabel="Update card"
            />
          </div>
        )}

        <div style={{ border: '1px solid #eee', borderRadius: 8, padding: '1rem', marginBottom: '1.5rem' }}>
          <h2 style={{ marginTop: 0 }}>Charge Details</h2>
          {summary?.items?.length ? (
            <div style={{ display: 'grid', gap: '0.75rem' }}>
              {summary.items.map((item) => (
                <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', borderBottom: '1px solid #f0f0f0', paddingBottom: '0.75rem' }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{item.description || 'Lesson charge'}</div>
                    <div style={{ color: '#666', fontSize: 13 }}>{item.teacher_name || 'Teacher'} / {item.scheduled_at ? new Date(item.scheduled_at).toLocaleString('en-PH') : 'Schedule not available'}</div>
                  </div>
                  <div style={{ fontWeight: 600 }}>{formatPhp(item.amount)}</div>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: '#666', marginBottom: 0 }}>No unpaid charges for this month.</p>
          )}
        </div>

        <div style={{ border: '1px solid #eee', borderRadius: 8, padding: '1rem' }}>
          <h2 style={{ marginTop: 0 }}>Payment History</h2>
          {history.length ? (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {history.map((invoice) => (
                  <tr key={invoice.id}>
                    <td>{invoice.billing_month}</td>
                    <td>{formatPhp(invoice.amount)}</td>
                    <td>{invoice.status}</td>
                    <td>{invoice.updated_at ? new Date(invoice.updated_at).toLocaleString('en-PH') : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p style={{ color: '#666', marginBottom: 0 }}>No payment history yet.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default BillingSetup;
